import * as React from 'react';
import { useState, useEffect } from 'react';
import { Box, List, ListItemButton, ListItemIcon, ListItemText, Typography, Badge, Collapse, ButtonBase } from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LanguageIcon from '@mui/icons-material/Language';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PeopleIcon from '@mui/icons-material/People';
import EngineeringIcon from '@mui/icons-material/Engineering';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import InventoryIcon from '@mui/icons-material/Inventory';
import BuildIcon from '@mui/icons-material/Build';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import TimeToLeaveIcon from '@mui/icons-material/TimeToLeave';
import GarageIcon from '@mui/icons-material/Garage';
import AssessmentIcon from '@mui/icons-material/Assessment';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import TireRepairIcon from '@mui/icons-material/TireRepair';
import FlagIcon from '@mui/icons-material/Flag';
import CampaignIcon from '@mui/icons-material/Campaign';
import EventNoteIcon from '@mui/icons-material/EventNote';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import ReviewsIcon from '@mui/icons-material/Reviews';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { withTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useUserStore } from '../../store';
import usePermissions from '../../hooks/usePermissions';
import { can, isSimplifiedUi } from '../../utils/access';
import WhatsNewDialog from '../whatsnew/WhatsNewDialog';

/**
 * One navigation list. `perm` is the registry key that shows an item (organizations/
 * permission_registry.py; the `menu.*` keys only hide menu entries, the API behind them stays
 * open). Items with a `simplifiedLabelKey` make up the "Omat työt" menu of the simplified UI,
 * which users who see only their own work orders get (utils/access.js isSimplifiedUi); every
 * other item they hold a key for follows below in its own section.
 */
const NAV_CATEGORIES = [
  {
    labelKey: 'nav.maintenanceOperations',
    items: [
      { path: '/', icon: DashboardIcon, labelKey: 'Dashboard', perm: 'work_orders.view', simplifiedLabelKey: 'nav.workQueue' },
      { path: '/calendar', icon: CalendarMonthIcon, labelKey: 'Calendar', perm: 'work_orders.view', simplifiedLabelKey: 'nav.mySchedule' },
      { path: '/quotes', icon: RequestQuoteIcon, labelKey: 'nav.quotes', perm: 'work_orders.view', simplifiedLabelKey: 'nav.quotes' },
      { path: '/maintenances', icon: DirectionsCarIcon, labelKey: 'Maintenances', perm: 'work_orders.view', simplifiedLabelKey: 'nav.myWorkOrders' },
      { path: '/supplier-orders', icon: ShoppingCartIcon, labelKey: 'nav.purchaseOrders', perm: 'menu.supplier_orders' },
      { path: '/employees', icon: EngineeringIcon, labelKey: 'Employees', perm: 'team.manage' },
      { path: '/work-time', icon: AccessTimeFilledIcon, labelKey: 'nav.workTime', perm: 'calendar.manage', simplifiedLabelKey: 'nav.workTime' },
    ],
  },
  {
    labelKey: 'nav.customers',
    items: [
      { path: '/customers', icon: PeopleIcon, labelKey: 'Customers', perm: 'menu.customers' },
      { path: '/customer-feedback', icon: ReviewsIcon, labelKey: 'customer_feedback.title', perm: 'menu.customer_feedback' },
      { path: '/service-reminders', icon: NotificationsActiveIcon, labelKey: 'service_reminders.tab_label', perm: 'menu.service_reminders' },
      { path: '/invoices', icon: ReceiptIcon, labelKey: 'Invoices', perm: 'invoices.view' },
      { path: '/ostolaskut', icon: ReceiptLongIcon, labelKey: 'purchaseLedger.navLabel', perm: 'menu.purchase_invoices' },
      { path: '/ai-phone', icon: PhoneInTalkIcon, labelKey: 'nav.aiPhone', perm: 'menu.ai_phone' },
    ],
  },
  {
    labelKey: 'nav.partsSales',
    items: [
      { path: '/kassamyynti', icon: PointOfSaleIcon, labelKey: 'parts_sales.counter_sale', perm: 'parts_sales.manage' },
      { path: '/lahetteet', icon: LocalShippingIcon, labelKey: 'parts_sales.deliveries', perm: 'parts_sales.manage' },
    ],
  },
  {
    labelKey: 'nav.inventoryManagement',
    items: [
      { path: '/inventory', icon: WarehouseIcon, labelKey: 'inventory.title', perm: 'menu.inventory' },
    ],
  },
  {
    labelKey: 'nav.tireHotel',
    items: [
      { path: '/tire-hotel', icon: TireRepairIcon, labelKey: 'tire_hotel.hotel_cards', perm: 'menu.tire_hotel' },
      { path: '/tire-hotel/locations', icon: WarehouseIcon, labelKey: 'tire_hotel.storage_locations', perm: 'menu.tire_hotel' },
      { path: '/tire-hotel/pipeline', icon: FlagIcon, labelKey: 'tire_hotel.sales_pipeline', perm: 'menu.tire_hotel' },
      { path: '/tire-hotel/campaigns', icon: CampaignIcon, labelKey: 'tire_hotel.campaigns', perm: 'menu.tire_hotel' },
    ],
  },
  {
    labelKey: 'nav.booking',
    items: [
      { path: '/bookings', icon: EventNoteIcon, labelKey: 'booking.bookings', perm: 'menu.bookings' },
    ],
  },
  {
    labelKey: 'nav.website',
    items: [
      { path: '/nettisivut', icon: LanguageIcon, labelKey: 'website.nav_label', perm: 'menu.website' },
    ],
  },
  {
    labelKey: 'nav.registries',
    items: [
      { path: '/jobs', icon: BuildIcon, labelKey: 'Jobs', perm: 'menu.jobs' },
      { path: '/huoltopaketit', icon: Inventory2Icon, labelKey: 'huoltopaketit.navLabel', perm: 'service_packages.manage' },
      { path: '/parts', icon: InventoryIcon, labelKey: 'Parts', perm: 'menu.parts' },
      { path: '/vendors', icon: LocalShippingIcon, labelKey: 'nav.partsSuppliers', perm: 'menu.vendors' },
      { path: '/catalogues', icon: MenuBookIcon, labelKey: 'catalogues.title', perm: 'menu.catalogues' },
      { path: '/vehicles', icon: TimeToLeaveIcon, labelKey: 'nav.vehicles', perm: 'menu.vehicles' },
      { path: '/service-bays', icon: GarageIcon, labelKey: 'nav.serviceBays', perm: 'menu.service_bays' },
      { path: '/imports', icon: FileUploadIcon, labelKey: 'Imports', perm: 'imports.manage' },
    ],
  },
  {
    labelKey: 'Reports',
    items: [
      { path: '/reports', icon: AssessmentIcon, labelKey: 'Reports', perm: 'reports.view' },
    ]
  }
];

const SIMPLIFIED_SECTION_KEY = 'nav.myWork';

/**
 * Navigation categories for the current organization: permission first, then the feature
 * flags that hide whole entries.
 */
function getNavCategories(currentOrganization) {
  const workHoursEnabled = currentOrganization?.work_hours_reporting_enabled ?? true;
  const inventoryEnabled = currentOrganization?.inventory_tracking_enabled ?? false;
  const tireHotelEnabled = currentOrganization?.tire_hotel_enabled ?? false;
  // Beta gates — features hidden entirely unless org is in beta program
  const tireHotelBeta = currentOrganization?.tire_hotel_beta ?? false;
  const bookingBeta = currentOrganization?.booking_beta ?? false;

  // Build list of paths to filter out based on disabled features
  const disabledPaths = [];
  if (!workHoursEnabled) {
    disabledPaths.push('/work-time');
  }
  if (!inventoryEnabled) {
    disabledPaths.push('/inventory');
    disabledPaths.push('/vendors');
  }
  // Supplier orders stay visible for orgs receiving inbound delivery notes
  // (sähköiset lähetteet) even without an ordering integration — exactly the
  // AutoFutur-varaosaliike customers the lähete feature targets.
  if (
    !currentOrganization?.has_supplier_integrations &&
    !currentOrganization?.inbound_delivery_notes_available
  ) {
    disabledPaths.push('/supplier-orders');
  }
  if (!currentOrganization?.ai_phone_enabled) {
    disabledPaths.push('/ai-phone');
  }
  if (!tireHotelBeta || !tireHotelEnabled) {
    disabledPaths.push('/tire-hotel');
    disabledPaths.push('/tire-hotel/locations');
    disabledPaths.push('/tire-hotel/pipeline');
    disabledPaths.push('/tire-hotel/campaigns');
  }
  if (!bookingBeta) {
    disabledPaths.push('/bookings');
  }
  // Nettisivut pilot: staff-set beta flag (users/me resolves it through has_feature).
  if (!currentOrganization?.website_beta) {
    disabledPaths.push('/nettisivut');
  }
  if (!currentOrganization?.customer_feedback_enabled) {
    disabledPaths.push('/customer-feedback');
  }
  // Combined flag: entitlement (admin beta flag or gen-4 Pro/Enterprise plan grant)
  // AND the org's own Features-settings toggle (backend-authoritative).
  if (!currentOrganization?.service_reminders_available) {
    disabledPaths.push('/service-reminders');
  }
  if (!currentOrganization?.vendor_catalogues_enabled) {
    disabledPaths.push('/catalogues');
  }
  // Ostoreskontra: entitlement AND activation (backend-authoritative combined flag).
  if (!currentOrganization?.purchase_ledger_available) {
    disabledPaths.push('/ostolaskut');
  }
  // Quotes only get their own menu item when the org opts in; otherwise they stay in the
  // work orders list, where the state filter covers them.
  if (!currentOrganization?.separate_quotes_list_enabled) {
    disabledPaths.push('/quotes');
  }
  // Parts sales, backend-authoritative combined flags: Kassamyynti is Perus+
  // (counter_sales_available), Lähetteet stays Pro+ (parts_sales_available).
  // A Perus org keeps the OSAMYYNTI section with Kassamyynti as its only item.
  if (!currentOrganization?.counter_sales_available) {
    disabledPaths.push('/kassamyynti');
  }
  if (!currentOrganization?.parts_sales_available) {
    disabledPaths.push('/lahetteet');
  }
  const allowed = (item) => can(currentOrganization, item.perm) && !disabledPaths.includes(item.path);

  if (isSimplifiedUi(currentOrganization)) {
    const ownWork = NAV_CATEGORIES.flatMap((category) => category.items)
      .filter((item) => item.simplifiedLabelKey && allowed(item))
      .map((item) => ({ ...item, labelKey: item.simplifiedLabelKey }));
    // Anything the organization has granted on top of the own-work keys (a "Näytä valikossa"
    // row, reports, invoices...) keeps its own section below the own-work list.
    const extra = NAV_CATEGORIES.map((category) => ({
      ...category,
      items: category.items.filter((item) => !item.simplifiedLabelKey && allowed(item)),
    })).filter((category) => category.items.length > 0);
    const sections = ownWork.length > 0 ? [{ labelKey: SIMPLIFIED_SECTION_KEY, items: ownWork }] : [];
    return [...sections, ...extra];
  }

  return NAV_CATEGORIES.map((category) => ({
    ...category,
    items: category.items.filter(allowed),
  })).filter((category) => category.items.length > 0);
}

function MainNavigation({ t, onNavigate }) {
  const location = useLocation();
  const { user, currentOrganization } = useUserStore();
  usePermissions();
  const isStaff = user?.is_staff || user?.is_superuser;
  const navCategories = getNavCategories(currentOrganization);

  const NAV_COLLAPSED_KEY = 'verstaani_nav_collapsed';
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem(NAV_COLLAPSED_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });

  const toggleSection = (labelKey) => {
    setCollapsed(prev => {
      const next = { ...prev, [labelKey]: !prev[labelKey] };
      localStorage.setItem(NAV_COLLAPSED_KEY, JSON.stringify(next));
      return next;
    });
  };

  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const [whatsNewUnreadCount, setWhatsNewUnreadCount] = useState(0);
  const [lastViewedAt, setLastViewedAt] = useState(null);
  const [pendingBookingCount, setPendingBookingCount] = useState(0);
  const [inboundNoteCount, setInboundNoteCount] = useState(0);
  const [purchaseInvoiceCount, setPurchaseInvoiceCount] = useState(0);

  useEffect(() => {
    fetchWhatsNewCount();
  }, []);

  useEffect(() => {
    if (!currentOrganization?.booking_beta) return;
    fetchPendingBookingCount();
    const interval = setInterval(fetchPendingBookingCount, 60000);
    return () => clearInterval(interval);
  }, [currentOrganization?.booking_beta]);

  useEffect(() => {
    if (!currentOrganization?.inbound_delivery_notes_available) {
      setInboundNoteCount(0);
      return;
    }
    fetchInboundNoteCount();
    const interval = setInterval(fetchInboundNoteCount, 60000);
    return () => clearInterval(interval);
  }, [currentOrganization?.inbound_delivery_notes_available]);

  const fetchWhatsNewCount = async () => {
    try {
      // Lightweight count-only endpoint — avoids downloading the full What's New
      // payload (~27 KB) just to render the unread badge.
      const response = await axios.get('/api/users/whatsnew/unread-count/', { withCredentials: true });
      setWhatsNewUnreadCount(response.data.unread_count || 0);
      setLastViewedAt(response.data.last_viewed_at);
    } catch (error) {
      console.error('Error fetching what\'s new count:', error);
    }
  };

  const fetchPendingBookingCount = async () => {
    try {
      const response = await axios.get('/api/bookings/pending_count/');
      setPendingBookingCount(response.data.count || 0);
    } catch (error) {
      // Silently ignore - user may not have booking permission
    }
  };

  useEffect(() => {
    if (!currentOrganization?.purchase_ledger_available || !can(currentOrganization, 'menu.purchase_invoices') || !can(currentOrganization, 'purchase_invoices.view')) {
      setPurchaseInvoiceCount(0);
      return;
    }
    fetchPurchaseInvoiceCount();
    const interval = setInterval(fetchPurchaseInvoiceCount, 60000);
    return () => clearInterval(interval);
  }, [currentOrganization?.purchase_ledger_available, currentOrganization?.organization_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPurchaseInvoiceCount = async () => {
    try {
      const response = await axios.get('/api/purchase-ledger/invoices/summary/');
      setPurchaseInvoiceCount((response.data.received || 0) + (response.data.flagged || 0));
    } catch (error) {
      // Silently ignore — badge just won't show
    }
  };

  const fetchInboundNoteCount = async () => {
    try {
      const response = await axios.get('/api/inbound-delivery-notes/counts/');
      setInboundNoteCount(response.data.new || 0);
    } catch (error) {
      // Silently ignore — badge just won't show
    }
  };

  const handleWhatsNewClose = () => {
    setWhatsNewOpen(false);
    setWhatsNewUnreadCount(0);
  };

  const handleLogout = () => {
    // Clear localStorage before logout
    localStorage.removeItem('currentOrganizationId');
    // Navigate to logout URL
    window.location.href = '/accounts/logout/';
  };

  // Collect all nav paths so we can do exact-prefix matching
  // (avoid /tire-hotel matching /tire-hotel/locations when both are nav items)
  const allNavPaths = navCategories.flatMap(c => c.items.map(i => i.path));

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    if (location.pathname === path) return true;
    // Only match as prefix if the sub-path isn't its own nav item
    if (location.pathname.startsWith(path + '/')) {
      // Check if a more specific nav path matches
      const moreSpecific = allNavPaths.some(
        p => p !== path && p.startsWith(path + '/') && location.pathname.startsWith(p)
      );
      return !moreSpecific;
    }
    return false;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {/* Main Navigation */}
      <Box sx={{ py: 2, px: 1.5 }}>
        {navCategories.map((category, categoryIndex) => {
          const isCollapsed = !!collapsed[category.labelKey];
          const hasActiveItem = isCollapsed && category.items.some(item => isActive(item.path));
          return (
            <Box key={category.labelKey} sx={{ mb: categoryIndex < navCategories.length - 1 ? 2 : 0 }}>
              <ButtonBase
                onClick={() => toggleSection(category.labelKey)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  px: 2,
                  mb: 0.5,
                  borderRadius: '4px',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' },
                }}
              >
                <Typography
                  variant="overline"
                  sx={{
                    color: hasActiveItem ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.5)',
                    fontSize: '11px',
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                  }}
                >
                  {t(category.labelKey)}
                </Typography>
                {isCollapsed ? (
                  <ExpandMoreIcon sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 16 }} />
                ) : (
                  <ExpandLessIcon sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 16 }} />
                )}
              </ButtonBase>
              <Collapse in={!isCollapsed} timeout="auto">
                <List component="nav" disablePadding>
                  {category.items.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.path);
                    const badgeCount =
                      item.path === '/bookings'
                        ? pendingBookingCount
                        : item.path === '/supplier-orders'
                        ? inboundNoteCount
                        : item.path === '/ostolaskut'
                        ? purchaseInvoiceCount
                        : 0;

                    return (
                      <ListItemButton
                        key={item.path}
                        data-testid={`nav-${item.path === '/' ? 'dashboard' : item.path.replace('/', '')}`}
                        component={Link}
                        to={item.path}
                        selected={active}
                        onClick={onNavigate}
                        sx={{
                          borderRadius: '8px',
                          mb: 0.5,
                          py: 1.5,
                          px: 2,
                          color: active ? 'white' : 'rgba(255,255,255,0.7)',
                          bgcolor: active ? 'rgba(255,255,255,0.15)' : 'transparent',
                          '&:hover': {
                            bgcolor: active ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.1)',
                          },
                          '&.Mui-selected': {
                            bgcolor: 'rgba(255,255,255,0.15)',
                            '&:hover': {
                              bgcolor: 'rgba(255,255,255,0.2)',
                            },
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                          {badgeCount > 0 ? (
                            <Badge badgeContent={badgeCount} color="error">
                              <Icon fontSize="small" />
                            </Badge>
                          ) : (
                            <Icon fontSize="small" />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={t(item.labelKey)}
                          primaryTypographyProps={{
                            fontSize: '15px',
                            fontWeight: active ? 500 : 400,
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                          }}
                        />
                        {active && (
                          <Box
                            sx={{
                              width: 4,
                              height: 20,
                              bgcolor: 'secondary.main',
                              borderRadius: '2px',
                              ml: 1,
                            }}
                          />
                        )}
                      </ListItemButton>
                    );
                  })}
                </List>
              </Collapse>
            </Box>
          );
        })}
      </Box>

      {/* Bottom Section */}
      <Box sx={{ py: 2, px: 1.5, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <List component="nav" disablePadding>
          <ListItemButton
            onClick={() => setWhatsNewOpen(true)}
            sx={{
              borderRadius: '8px',
              mb: 0.5,
              py: 1.5,
              px: 2,
              color: 'rgba(255,255,255,0.7)',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
              <Badge badgeContent={whatsNewUnreadCount} color="error">
                <NewReleasesIcon fontSize="small" />
              </Badge>
            </ListItemIcon>
            <ListItemText
              primary={t('nav.whatsNew')}
              primaryTypographyProps={{
                fontSize: '15px',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}
            />
          </ListItemButton>

          {isStaff && (
            <ListItemButton
              component={Link}
              to="/vadmin"
              selected={isActive('/vadmin')}
              onClick={onNavigate}
              data-testid="nav-vadmin"
              sx={{
                borderRadius: '8px',
                mb: 0.5,
                py: 1.5,
                px: 2,
                color: isActive('/vadmin') ? 'white' : 'rgba(255,255,255,0.7)',
                bgcolor: isActive('/vadmin') ? 'rgba(255,255,255,0.15)' : 'transparent',
                '&:hover': {
                  bgcolor: isActive('/vadmin') ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.1)',
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                <AdminPanelSettingsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t('vadmin.title')}
                primaryTypographyProps={{
                  fontSize: '15px',
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                }}
              />
            </ListItemButton>
          )}

          <ListItemButton
            component={Link}
            to="/settings"
            selected={isActive('/settings')}
            onClick={onNavigate}
            sx={{
              borderRadius: '8px',
              mb: 0.5,
              py: 1.5,
              px: 2,
              color: isActive('/settings') ? 'white' : 'rgba(255,255,255,0.7)',
              bgcolor: isActive('/settings') ? 'rgba(255,255,255,0.15)' : 'transparent',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t('Settings')}
              primaryTypographyProps={{
                fontSize: '15px',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}
            />
          </ListItemButton>

          <ListItemButton
            data-testid="logout-button"
            onClick={handleLogout}
            sx={{
              borderRadius: '8px',
              py: 1.5,
              px: 2,
              color: 'rgba(255,255,255,0.7)',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t('Log out')}
              primaryTypographyProps={{
                fontSize: '15px',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}
            />
          </ListItemButton>
        </List>
      </Box>

      <WhatsNewDialog
        open={whatsNewOpen}
        onClose={handleWhatsNewClose}
        lastViewedAt={lastViewedAt}
      />
    </Box>
  );
}

export default withTranslation()(MainNavigation);
